import { z } from 'zod';

const LOCALE = 'en-US';
const OPTIONS_DATE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
const OPTIONS_DATE_UTC: Intl.DateTimeFormatOptions = { ...OPTIONS_DATE, timeZone: 'UTC' };
const OPTIONS_MONTH_YEAR: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
const OPTIONS_MONTH_YEAR_UTC: Intl.DateTimeFormatOptions = { ...OPTIONS_MONTH_YEAR, timeZone: 'UTC' };

const dateStringSchema = z.string().refine((val) => !isNaN(Date.parse(val)));

function formatWithOptions(dateString: string, options: Intl.DateTimeFormatOptions): string {
  const { success, data } = dateStringSchema.safeParse(dateString);
  if (!success) {
    throw new Error(`Invalid date string: "${dateString}"`);
  }

  return new Intl.DateTimeFormat(LOCALE, options).format(new Date(data));
}

export function formatDate(dateString: string): string {
  return formatWithOptions(dateString, OPTIONS_DATE);
}

export function formatDateUtc(dateString: string): string {
  return formatWithOptions(dateString, OPTIONS_DATE_UTC);
}

export function formatMonthYear(dateString: string): string {
  return formatWithOptions(dateString, OPTIONS_MONTH_YEAR);
}

export function formatMonthYearUtc(dateString: string): string {
  return formatWithOptions(dateString, OPTIONS_MONTH_YEAR_UTC);
}

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const getMonthName = (month: number | string): string => {
  const monthIndex = typeof month === 'string' ? parseInt(month, 10) - 1 : month - 1;
  return months[monthIndex] || '';
};

const OPTIONS_DATE_TIME: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

export function formatVersionDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(LOCALE, OPTIONS_DATE_TIME).format(dateObj);
}

const OPTIONS_MONTH_DAY: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
};

export function formatMonthDay(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(LOCALE, OPTIONS_MONTH_DAY).format(dateObj);
}

const OPTIONS_DATE_TIME_FULL: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/**
 * Safely formats a date/time value, returning a fallback for invalid inputs.
 * Accepts string, Date, null, or undefined.
 *
 * @param value - The value to format
 * @param fallback - The fallback string to return if value is invalid (default: '—')
 * @returns Formatted date string or fallback
 */
export function safeFormatDateTime(value: unknown, fallback = '—'): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value !== 'string' && !(value instanceof Date)) return fallback;

  try {
    const dateObj = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(dateObj.getTime())) return fallback;
    return new Intl.DateTimeFormat(LOCALE, OPTIONS_DATE_TIME_FULL).format(dateObj);
  } catch {
    return fallback;
  }
}
